import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/features/common/ui';
import { LegalShell, List, Mail, Section } from './LegalShell';
import { mcpUrl } from './legal-facts';

/**
 * The public page about connecting an assistant to Fint: `/docs/mcp`.
 *
 * ── WHY THIS PATH ──
 * The OAuth metadata has advertised `${issuer}/docs/mcp` as the service and
 * resource documentation since the connector shipped (oauth.service.ts), and
 * until this page existed the SPA's catch-all sent that link to the login.
 * The directories ask for a documentation URL, and the one Fint already
 * publishes is the right one to give them.
 *
 * It is the signed-out, plain-words version of docs/mcp.md - what to paste,
 * what the assistant can and cannot do, how to stop it - with example
 * prompts, which OpenAI's directory asks for in place of screenshots. The
 * capability claims are the ones e2e/mcp.api.spec.ts enforces; a tool that
 * changes them changes this page too.
 */
export function ConnectPage() {
  const url = mcpUrl();
  const [copied, setCopied] = useState(false);

  return (
    <LegalShell title="Connect an AI assistant to Fint">
      <p>
        Fint works with AI assistants that speak MCP, the open standard for connecting them to apps: Claude, ChatGPT and
        others. Once connected, you can ask about your books in plain words (who owes you, how cash looks next month, what
        GST is due) and have the assistant draft invoices and expenses for you to approve.
      </p>

      <Section title="The address to paste">
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3">
          <code className="flex-1 overflow-x-auto font-mono text-sm" data-mcp-url>
            {url}
          </code>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void navigator.clipboard?.writeText(url);
              setCopied(true);
            }}
          >
            {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">Exactly as written, with no slash at the end.</p>
      </Section>

      <Section title="In Claude">
        <List>
          <li>Open Settings &rarr; Connectors &rarr; Add custom connector, and paste the address.</li>
          <li>Sign in to Fint when asked, tick the books Claude may see, and approve.</li>
        </List>
      </Section>

      <Section title="In ChatGPT">
        <List>
          <li>
            Open Settings &rarr; Apps, and add a custom app with the address above. On some plans this sits under Advanced
            settings &rarr; Developer mode.
          </li>
          <li>Sign in to Fint when asked, tick the books ChatGPT may see, and approve.</li>
        </List>
      </Section>

      <Section title="Other assistants and scripts">
        <p>
          Anything that takes an MCP server address can connect the same way. A tool that wants a key instead of a sign-in can
          use one made in Fint under Settings &rarr; API keys and AI agents, sent as <code>Authorization: Bearer &lt;key&gt;</code>.
        </p>
      </Section>

      <Section title="What it can do">
        <List>
          <li>
            <strong>Read</strong> the books you allowed: invoices, customers and their ledgers, expenses, bank balances, who owes
            what and how late, profit and loss, GSTR-1 and GSTR-3B, TDS and TCS, exports, and Fint&rsquo;s CFO view of cash,
            runway, dues and debts.
          </li>
          <li>
            <strong>Propose</strong> records from what you tell or show it: a draft invoice, an expense from a bill, a deposit, a
            customer, a vendor, a correction to a recorded row. Each waits in Fint &rarr; Inbox until a person confirms it.
          </li>
          <li>
            <strong>File</strong> records under projects and tags, attach a receipt, and raise or answer review questions.
          </li>
          <li>
            <strong>Answer the CFO page</strong>: questions typed there, the month&rsquo;s review and payment-reminder wording
            wait for your assistant, which picks them up the next time it connects. Fint checks every amount in an answer
            against its own figures before you see it.
          </li>
        </List>
      </Section>

      <Section title="What it cannot do">
        <p>
          It cannot send or issue a document, email or message a customer, record or move money, void or delete a record,
          confirm its own proposals, lock a period or change a tax rate. Those stay with people, in Fint. This is enforced by
          what the connector&rsquo;s code can reach, not by asking the assistant nicely.
        </p>
      </Section>

      <Section title="Things to ask">
        <List>
          <li>&ldquo;Who owes me money, and how late are they?&rdquo;</li>
          <li>&ldquo;How many months of runway do I have at this month&rsquo;s spending?&rdquo;</li>
          <li>&ldquo;What is my GST liability for September, and what makes it up?&rdquo;</li>
          <li>&ldquo;Draft an invoice to Acme for 20 hours of design at &#8377;2,500 an hour.&rdquo;</li>
          <li>&ldquo;Here is a fuel bill. Add it as an expense.&rdquo;</li>
          <li>&ldquo;Answer the questions waiting on my CFO page.&rdquo;</li>
        </List>
      </Section>

      <Section title="Your control">
        <List>
          <li>You choose the books on a Fint screen that shows exactly what is being asked for.</li>
          <li>
            You can disconnect at any time in Settings &rarr; Connected assistants. It stops working on its very next request.
          </li>
          <li>A person with the Auditor role cannot connect an assistant: that seat is read-only in Fint itself.</li>
          <li>
            What your assistant reads is handled by its provider under their terms. See our{' '}
            <Link className="text-primary hover:underline" data-tap-exempt="text link in a sentence" to="/privacy">
              privacy policy
            </Link>
            .
          </li>
        </List>
      </Section>

      <Section title="Help">
        <p>
          Write to <Mail />.
        </p>
      </Section>
    </LegalShell>
  );
}
